import React, { createContext, useContext } from "react"
import { BaseEdge, EdgeLabelRenderer, getBezierPath } from "reactflow"

/**
 * Custom edge for the flow canvas: a curved bezier connection that carries its
 * routing-condition label and a click-to-delete button.
 *
 * The delete handler is supplied through context rather than per-edge `data` so
 * it never gets serialised into Firestore (functions can't be persisted) and so
 * `EDGE_TYPES` can stay a stable module-level reference (reactflow requirement).
 *
 * The × button shows when the edge is hovered or selected — so the user can
 * click the connecting line (which selects it) and then delete it.
 */

export const FlowEdgeContext = createContext({ onDeleteEdge: null })

function DeletableEdge({
	id,
	sourceX,
	sourceY,
	targetX,
	targetY,
	sourcePosition,
	targetPosition,
	markerEnd,
	style,
	label,
	selected,
}) {
	const { onDeleteEdge } = useContext(FlowEdgeContext)

	const [edgePath, labelX, labelY] = getBezierPath({
		sourceX,
		sourceY,
		sourcePosition,
		targetX,
		targetY,
		targetPosition,
	})

	const edgeStyle = {
		...style,
		stroke: selected ? "#6366f1" : style?.stroke,
		strokeWidth: selected ? 2 : style?.strokeWidth,
	}

	return (
		<>
			<BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={edgeStyle} />
			<EdgeLabelRenderer>
				<div
					className={["ai-flow-edge-label", selected ? "is-selected" : ""]
						.filter(Boolean)
						.join(" ")}
					style={{
						transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
					}}>
					{label ? <span className="ai-flow-edge-text">{label}</span> : null}
					<button
						type="button"
						className="ai-flow-edge-delete"
						title="Delete connection"
						aria-label="Delete connection"
						onClick={(event) => {
							event.stopPropagation()
							onDeleteEdge?.(id)
						}}>
						×
					</button>
				</div>
			</EdgeLabelRenderer>
		</>
	)
}

// Stable module-level map — reactflow re-validates node/edge types by reference.
export const EDGE_TYPES = { flow: DeletableEdge }
